import React from 'react'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import Grid from '@material-ui/core/Grid'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import FileUploadIcon from 'components/material-symbol/icons/FileUpload'
import VideocamIcon from 'components/material-symbol/icons/Videocam'
import { TonalButton } from 'components/empty-page-state'
import { PageHeader } from 'components/page-header'
import SectionEmptyState from 'components/section-empty-state'
import { getInitials } from 'lib/text-display'
import pluralize from 'lib/pluralize'
import RouterLinkButton from 'components/router-link-button'
import { useMyVirtualCoachingRequests } from './api-hooks'
import StudentStatusChip, {
  getStatusChipClassName,
} from './request-status-chip'
import { RequestLoadError } from './request-state-messages'
import { useStyles } from './styles'
import {
  formatDueDate,
  getRequestPath,
  getUploadPath,
  REQUEST_STATUS,
} from './utils'

// Requests that need the golfer to act come first.
const STATUS_ORDER = [
  REQUEST_STATUS.PURCHASED,
  REQUEST_STATUS.READY_TO_ANALYZE,
  REQUEST_STATUS.COMPLETE,
]

export const sortRequests = requests =>
  requests
    .map((request, index) => ({ request, index }))
    .sort(
      (a, b) =>
        STATUS_ORDER.indexOf(a.request.status) -
          STATUS_ORDER.indexOf(b.request.status) || a.index - b.index,
    )
    .map(({ request }) => request)

export const getRequestCaption = request => {
  if (request.status === REQUEST_STATUS.PURCHASED) {
    return 'Upload your swing videos to get started'
  }
  if (request.status === REQUEST_STATUS.READY_TO_ANALYZE) {
    const due = formatDueDate(request.dueAt)
    return due ? `Coach reviewing · expected by ${due}` : 'Coach reviewing'
  }
  return 'Feedback from your coach is ready'
}

const RequestCard = ({ request }) => {
  const classes = useStyles()
  const { lessonType } = request
  const coach = lessonType?.coach
  const titleId = `request-title-${request.id}`

  return (
    <Card elevation={0} className={classes.requestCard}>
      <Box className={classes.requestBadgeRow}>
        <StudentStatusChip
          status={request.status}
          size="small"
          className={getStatusChipClassName(classes, request.status)}
        />
      </Box>
      <Typography component="h3" id={titleId} className={classes.requestTitle}>
        <RouterLink
          to={getRequestPath(request.id)}
          className={classes.requestLink}
        >
          {lessonType?.title}
        </RouterLink>
      </Typography>
      <Box className={classes.requestMeta}>
        {coach?.name ? (
          <Box className={classes.requestCoach}>
            <Avatar
              className={classes.requestCoachAvatar}
              alt=""
              src={coach.coachProfile?.profilePhoto || undefined}
            >
              {getInitials(coach.name)}
            </Avatar>
            <Typography variant="caption" className={classes.requestText}>
              {coach.name}
            </Typography>
          </Box>
        ) : null}
        {request.orderNumber ? (
          <Typography variant="caption" className={classes.requestText}>
            Order #{request.orderNumber}
          </Typography>
        ) : null}
        <Typography variant="caption" className={classes.requestText}>
          {getRequestCaption(request)}
        </Typography>
      </Box>
      {request.status === REQUEST_STATUS.PURCHASED ? (
        <Box className={classes.requestFooter}>
          <RouterLinkButton
            variant="contained"
            color="primary"
            to={getUploadPath(request.id)}
            className={classes.requestAction}
            startIcon={<FileUploadIcon />}
            aria-describedby={titleId}
          >
            Upload your videos
          </RouterLinkButton>
        </Box>
      ) : null}
    </Card>
  )
}

RequestCard.propTypes = { request: PropTypes.object.isRequired }

const RequestsList = () => {
  const classes = useStyles()
  const { data, loading, error, refetch } = useMyVirtualCoachingRequests()
  const requests = data?.myVirtualCoachingRequests

  let content
  if (loading && !requests) {
    content = <LinearProgress color="primary" aria-label="Loading" />
  } else if (error && !requests) {
    content = <RequestLoadError error={error} onRetry={() => refetch()} />
  } else if (!requests || requests.length === 0) {
    content = (
      <Box className={classes.listEmpty}>
        <SectionEmptyState
          Icon={VideocamIcon}
          title="No Virtual Coaching requests yet"
          caption="When you buy Virtual Coaching from your coach, it shows up here so you can upload your videos and read their feedback."
          bordered={false}
          action={
            <TonalButton to="/club" color="primary">
              Go to My Coach
            </TonalButton>
          }
        />
      </Box>
    )
  } else {
    content = (
      <>
        <Box className={classes.listToolbar}>
          <Typography
            component="h2"
            variant="subtitle1"
            className={classes.listTitle}
          >
            {requests.length} {pluralize(requests.length, 'Request')}
          </Typography>
        </Box>
        <Grid
          container
          spacing={2}
          component="ul"
          className={classes.listGrid}
          aria-label="Virtual coaching requests"
        >
          {sortRequests(requests).map(request => (
            <Grid
              item
              key={request.id}
              xs={12}
              md={6}
              lg={4}
              component="li"
              className={classes.listItem}
            >
              <RequestCard request={request} />
            </Grid>
          ))}
        </Grid>
      </>
    )
  }

  return (
    <>
      <PageHeader title="Virtual Coaching" />
      <Box className={classes.listRoot}>{content}</Box>
    </>
  )
}

export default RequestsList
